/**
 * Runtime polyfills for classroom interactive panels stuck on old Chromium.
 *
 * Affected devices (system WebView can never be updated):
 *   - SMART Board MX265-V2 — Android 8, discontinued
 *   - Clear Touch 6000K+   — Android 9, non-EDLA, no Play Store
 * Baseline engine: Chromium 60-75.
 *
 * Syntax-level support comes from @babel/preset-env + .browserslistrc, and the
 * bulk of the missing standard library comes from core-js via
 * `useBuiltIns: 'usage'`. This file covers only what core-js does not ship.
 *
 * Everything here is feature-detected, so current browsers use the native
 * implementation and pay nothing but the download.
 *
 * Imported first in src/index.tsx so it runs before the widget tree is pulled in.
 *
 * NOTE: distinct from src/helper/polyfill.ts, which is pdf.js-specific (worker
 * wiring + Map upsert shims) and unrelated to browser-version support.
 */
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
// Uint8Array hex/base64 (Chrome 140 / Firefox 133 / Safari 18.2). pdf.js calls
// `toBase64()` unguarded on the main thread; the same module is ALSO prepended
// to the pdf.js worker entry in webpack.worker.config.js, because the call that
// breaks every PDF background (`toHex` in PDFDocument.get fingerprints) runs in
// the worker's own realm where this one is invisible. See the module header and
// docs/features/browserSupport.md.
import './polyfills/uint8ArrayBase64';
// `:focus-visible` is Chrome 86+, and an unsupported pseudo-class invalidates the
// whole rule, so every focus style in src/styles/ disappeared below that version.
// postcss-focus-visible (see postcss.config.js) rewrites those rules to a
// class-based selector; this polyfill is what applies the class. Importing it
// unconditionally is deliberate — it is ~1 KB and no-ops where
// `:focus-visible` is supported natively.
import 'focus-visible';
/**
 * `structuredClone` is Chrome 98+ and core-js does not polyfill it, but the app
 * calls it in ~73 places (ArrayModel, ColoredTiles, GameController, WhiteBoard's
 * `analog` render branch) to snapshot widget data for undo/redo.
 *
 * Every one of those call sites clones plain save-state data: objects, arrays,
 * primitives, and occasionally Date. This fallback covers exactly that. It does
 * NOT support Map, Set, ArrayBuffer, RegExp or cyclic references — the real
 * structuredClone does. If a future call site needs those, replace this with a
 * full implementation rather than extending it piecemeal.
 */
function deepClone(value) {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (value instanceof Date) {
        return new Date(value.getTime());
    }
    if (Array.isArray(value)) {
        var out_1 = new Array(value.length);
        for (var i = 0; i < value.length; i++) {
            out_1[i] = deepClone(value[i]);
        }
        return out_1;
    }
    var out = {};
    var source = value;
    for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            out[key] = deepClone(source[key]);
        }
    }
    return out;
}
// `globalThis` itself is only Chrome 71+, which is inside the range we support,
// so resolve the global object the old way rather than depending on it (or on
// core-js having injected its globalThis shim ahead of this module).
var globalObject = typeof window !== 'undefined'
    ? window
    : typeof self !== 'undefined'
        ? self
        : // eslint-disable-next-line no-new-func
            Function('return this')();
if (typeof globalObject.structuredClone !== 'function') {
    globalObject.structuredClone = deepClone;
}
/**
 * ResizeObserver is Chrome 64+. Used unguarded in WhiteBoard.tsx (re-centres the
 * canvas after load) and helper/hooks/useResizeObserver.ts, so on Chromium 60-63
 * the missing constructor threw inside a useEffect. With no ErrorBoundary in the
 * app, that unmounts the entire React root.
 */
if (typeof globalObject.ResizeObserver !== 'function') {
    globalObject.ResizeObserver = ResizeObserverPolyfill;
}
